import React, { useState } from 'react';
import { useAuthenticator } from "@aws-amplify/ui-react";
import EnhancedSEO from '../../Components/SEO/EnhancedSEO'; // 🆕 Import Enhanced SEO component

const AboutPage = () => {
  const [expandedSection, setExpandedSection] = useState('our-story');
  const [showSection, setShowSection] = useState(false); // Flag to control team section visibility

  const { authStatus } = useAuthenticator(context => [context.authStatus]);

  const handleAccordionToggle = (sectionId) => {
    setExpandedSection(expandedSection === sectionId ? null : sectionId);
  };

  // All your existing styles remain exactly the same
  const containerStyle = {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '32px 16px',
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    lineHeight: 1.5,
    color: '#212121'
  };

  const headerStyle = {
    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #ec4899 100%)',
    color: 'white',
    padding: '32px',
    borderRadius: '8px',
    marginBottom: '32px',
    boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
  };

  const paperStyle = {
    backgroundColor: '#fff',
    padding: '16px',
    borderRadius: '8px',
    border: '1px solid #e0e0e0',
    marginBottom: '16px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
  };

  const accordionStyle = {
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    marginBottom: '8px',
    overflow: 'hidden'
  };

  const accordionHeaderStyle = {
    padding: '16px',
    cursor: 'pointer',
    backgroundColor: '#f5f5f5',
    borderBottom: '1px solid #e0e0e0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    transition: 'background-color 0.2s'
  };

  const accordionContentStyle = {
    padding: '24px',
    borderTop: '1px solid #e0e0e0'
  };

  const chipStyle = {
    display: 'inline-block',
    padding: '6px 12px',
    margin: '4px',
    borderRadius: '16px',
    fontSize: '14px',
    cursor: 'pointer',
    transition: 'all 0.2s'
  };

  const cardStyle = {
    backgroundColor: '#f5f5f5',
    padding: '16px',
    borderRadius: '8px',
    marginBottom: '16px',
    border: '1px solid #e0e0e0'
  };

  const alertStyle = {
    padding: '16px',
    borderRadius: '8px',
    marginBottom: '16px',
    border: '1px solid #e3f2fd',
    backgroundColor: '#e3f2fd',
    color: '#1976d2'
  };

  const successAlertStyle = {
    ...alertStyle,
    border: '1px solid #e8f5e8',
    backgroundColor: '#e8f5e8',
    color: '#388e3c'
  };

  const buttonStyle = {
    padding: '12px 24px',
    borderRadius: '4px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    margin: '4px',
    transition: 'all 0.2s'
  };

  const primaryButtonStyle = {
    ...buttonStyle,
    backgroundColor: '#6366f1',
    color: 'white'
  };

  const outlinedButtonStyle = {
    ...buttonStyle,
    backgroundColor: 'transparent',
    color: '#6366f1',
    border: '1px solid #6366f1'
  };

  const sections = [
    {
      id: 'our-story',
      title: 'Our Story & Mission',
      icon: '🚀',
      color: '#6366f1',
      content: (
        <div>
          <div style={{ ...cardStyle, backgroundColor: '#0f172a', color: 'white' }}>
            <h4 style={{ marginBottom: '12px', fontSize: '18px' }}>🤖 Our Primary Mission</h4>
            <p style={{ margin: 0, fontSize: '16px', fontWeight: '500' }}>
              To democratize artificial intelligence and LLM capabilities for small and local businesses, 
              giving them access to enterprise-level technology that was previously only available to corporations 
              with massive budgets and technical teams.
            </p>
          </div>

          <p style={{ marginBottom: '16px', fontSize: '16px' }}>
            Revfer was born from observing a critical gap in the market: while large corporations have access to 
            sophisticated AI tools, data analytics, and intelligent automation, small and local businesses are 
            left behind. We're changing that by making cutting-edge AI accessible, affordable, and practical 
            for every business owner.
          </p>

          <div style={cardStyle}>
            <h4 style={{ marginBottom: '12px', color: '#1976d2' }}>The AI Gap We're Closing</h4>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li>Small businesses lack access to AI-powered customer insights and analytics</li>
              <li>No affordable tools for AI-assisted content creation and marketing</li>
              <li>Limited ability to leverage customer data for business intelligence</li>
              <li>Expensive enterprise AI solutions out of reach for local businesses</li>
              <li>Complex technical barriers preventing AI adoption</li>
            </ul>
          </div>

          <div style={cardStyle}>
            <h4 style={{ marginBottom: '12px', color: '#7b1fa2' }}>Our AI-Powered Solution</h4>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li><strong>Intelligent Business Insights:</strong> AI analytics that help understand customer behavior and preferences</li>
              <li><strong>Content Generation:</strong> LLM-powered tools for creating marketing content, responses, and communications</li>
              <li><strong>Smart Customer Matching:</strong> AI algorithms that connect businesses with their ideal customers</li>
              <li><strong>Automated Reputation Management:</strong> AI-assisted review responses and sentiment analysis</li>
              <li><strong>Predictive Analytics:</strong> Machine learning insights to help businesses anticipate trends and opportunities</li>
            </ul>
          </div>

          <div style={cardStyle}>
            <h4 style={{ marginBottom: '12px', color: '#388e3c' }}>Beyond AI: Building Trust Through Community</h4>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li><strong>Authentic Reviews:</strong> Community-verified reviews that AI helps analyze and surface insights from</li>
              <li><strong>Trust Networks:</strong> AI-powered relationship mapping that weights recommendations by connection strength</li>
              <li><strong>Intelligent Discovery:</strong> Machine learning recommendations based on user behavior and preferences</li>
              <li><strong>Verified Connections:</strong> AI-assisted verification to ensure authentic business relationships</li>
              <li><strong>Local Focus:</strong> AI algorithms optimized for community-based business discovery</li>
            </ul>
          </div>

          <div style={successAlertStyle}>
            <h4 style={{ margin: '0 0 8px 0' }}>
              AI for Everyone, Not Just Tech Giants
            </h4>
            <p style={{ margin: 0, fontSize: '14px' }}>
              We believe that small businesses are the backbone of local economies and deserve access to the same 
              powerful AI tools that help large corporations thrive. Revfer is their gateway to intelligent technology.
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'how-it-works',
      title: 'How Revfer Works',
      icon: '⚙️',
      color: '#1976d2',
      content: (
        <div>
          <p style={{ marginBottom: '16px' }}>
            Revfer provides small and local businesses with enterprise-level AI capabilities while creating a trusted community ecosystem for authentic business discovery and customer engagement.
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px', marginBottom: '24px' }}>
            <div style={{ ...cardStyle, backgroundColor: '#0f172a', color: 'white' }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                <span style={{ fontSize: '32px', marginRight: '12px' }}>1️⃣</span>
                <h4 style={{ margin: 0 }}>Access AI-Powered Tools</h4>
              </div>
              <p style={{ margin: 0, fontSize: '14px' }}>
                Small businesses get instant access to AI analytics, content generation, customer insights, 
                and automated business intelligence previously only available to large corporations.
              </p>
            </div>

            <div style={{ ...cardStyle, backgroundColor: '#1e40af', color: 'white' }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                <span style={{ fontSize: '32px', marginRight: '12px' }}>2️⃣</span>
                <h4 style={{ margin: 0 }}>Build Community Trust</h4>
              </div>
              <p style={{ margin: 0, fontSize: '14px' }}>
                Connect with customers through AI-enhanced authentic reviews and community verification. 
                Our AI helps businesses understand and respond to feedback intelligently.
              </p>
            </div>

            <div style={{ ...cardStyle, backgroundColor: '#7c3aed', color: 'white' }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                <span style={{ fontSize: '32px', marginRight: '12px' }}>3️⃣</span>
                <h4 style={{ margin: 0 }}>Leverage Network Intelligence</h4>
              </div>
              <p style={{ margin: 0, fontSize: '14px' }}>
                AI algorithms analyze trust networks and customer relationships to provide businesses 
                with intelligent insights about their community and potential customers.
              </p>
            </div>

            <div style={{ ...cardStyle, backgroundColor: '#dc2626', color: 'white' }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                <span style={{ fontSize: '32px', marginRight: '12px' }}>4️⃣</span>
                <h4 style={{ margin: 0 }}>Grow with AI Insights</h4>
              </div>
              <p style={{ margin: 0, fontSize: '14px' }}>
                Make data-driven decisions with AI-powered analytics, predictive insights, and 
                automated recommendations that help optimize business operations and customer satisfaction.
              </p>
            </div>
          </div>

          <div style={cardStyle}>
            <h4 style={{ marginBottom: '12px', color: '#6366f1' }}>AI-Powered Business Intelligence</h4>
            <p style={{ marginBottom: '12px', fontSize: '14px' }}>
              Our platform provides small businesses with enterprise-level insights through AI analysis:
            </p>
            <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px' }}>
              <li><strong>Customer Sentiment Analysis:</strong> AI understanding of customer feedback and preferences</li>
              <li><strong>Competitive Intelligence:</strong> Machine learning insights about market positioning</li>
              <li><strong>Predictive Analytics:</strong> AI forecasting for business trends and opportunities</li>
              <li><strong>Automated Content Generation:</strong> LLM assistance for marketing and communication</li>
              <li><strong>Smart Recommendations:</strong> AI matching businesses with ideal customers</li>
            </ul>
          </div>

          <div style={cardStyle}>
            <h4 style={{ marginBottom: '12px', color: '#dc2626' }}>Beyond Reviews: Complete AI Ecosystem</h4>
            <p style={{ marginBottom: '12px', fontSize: '14px' }}>
              While reviews are part of our platform, our AI capabilities extend far beyond traditional review systems:
            </p>
            <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '14px' }}>
              <li><strong>Business Document Analysis:</strong> AI processing of contracts, invoices, and business documents</li>
              <li><strong>Customer Journey Mapping:</strong> Machine learning analysis of customer interactions</li>
              <li><strong>Automated Business Insights:</strong> AI-generated reports and recommendations</li>
              <li><strong>Intelligent Network Analysis:</strong> Understanding community connections and influence</li>
              <li><strong>Personalized Business Growth Plans:</strong> AI-driven strategies for local market expansion</li>
            </ul>
          </div>
        </div>
      )
    },
    {
      id: 'technology',
      title: 'Technology & Innovation',
      icon: '🤖',
      color: '#7b1fa2',
      content: (
        <div>
          <p style={{ marginBottom: '16px' }}>
            Revfer democratizes cutting-edge AI and LLM technology, making enterprise-level capabilities accessible and affordable for small and local businesses who previously couldn't access these powerful tools.
          </p>

          <div style={cardStyle}>
            <h4 style={{ marginBottom: '12px', color: '#1976d2' }}>Enterprise AI for Small Business</h4>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li><strong>LLM Integration:</strong> Advanced language models for content creation, customer communication, and business intelligence</li>
              <li><strong>Business Document AI:</strong> Automated processing and analysis of contracts, invoices, and business documents</li>
              <li><strong>Customer Intelligence:</strong> AI-powered insights into customer behavior, preferences, and satisfaction patterns</li>
              <li><strong>Predictive Analytics:</strong> Machine learning forecasts for sales, trends, and business opportunities</li>
              <li><strong>Automated Insights:</strong> AI-generated reports and recommendations that traditionally required expensive consultants</li>
            </ul>
          </div>

          <div style={cardStyle}>
            <h4 style={{ marginBottom: '12px', color: '#7b1fa2' }}>AI-Powered Review & Discovery Intelligence</h4>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li><strong>Sentiment Analysis:</strong> AI understanding of customer feedback across all touchpoints</li>
              <li><strong>Review Enhancement:</strong> LLM assistance for both writing and responding to reviews</li>
              <li><strong>Vector Embeddings:</strong> Semantic search across reviews, documents, and business information</li>
              <li><strong>Natural Language Queries:</strong> Ask complex business questions in plain English</li>
              <li><strong>Competitive Intelligence:</strong> AI analysis of market positioning and competitor insights</li>
            </ul>
          </div>

          <div style={cardStyle}>
            <h4 style={{ marginBottom: '12px', color: '#388e3c' }}>Intelligent Automation & Optimization</h4>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li><strong>Smart Customer Matching:</strong> AI algorithms that connect businesses with ideal customers</li>
              <li><strong>Automated Content Generation:</strong> LLM-powered marketing materials, social media posts, and communications</li>
              <li><strong>Business Process Optimization:</strong> AI recommendations for improving operations and efficiency</li>
              <li><strong>Dynamic Pricing Intelligence:</strong> Machine learning insights for competitive pricing strategies</li>
              <li><strong>Growth Opportunity Detection:</strong> AI identification of expansion and improvement opportunities</li>
            </ul>
          </div>

          <div style={cardStyle}>
            <h4 style={{ marginBottom: '12px', color: '#f57c00' }}>Trust & Verification AI</h4>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li><strong>Network Analysis:</strong> AI mapping and analysis of trust relationships and community connections</li>
              <li><strong>Fraud Detection:</strong> Machine learning identification of fake reviews and manipulated content</li>
              <li><strong>Community Verification:</strong> AI-assisted crowdsourced validation of business information</li>
              <li><strong>Reputation Management:</strong> Intelligent monitoring and response recommendations for online presence</li>
              <li><strong>Credibility Scoring:</strong> AI algorithms that calculate trust and reliability metrics</li>
            </ul>
          </div>

          <div style={cardStyle}>
            <h4 style={{ marginBottom: '12px', color: '#dc2626' }}>Infrastructure & Accessibility</h4>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li><strong>AWS Serverless:</strong> Enterprise-grade infrastructure without enterprise costs</li>
              <li><strong>No-Code AI Tools:</strong> Complex AI capabilities accessible through simple interfaces</li>
              <li><strong>Real-time Processing:</strong> Instant AI insights and recommendations</li>
              <li><strong>Scalable Solutions:</strong> AI that grows with your business needs</li>
              <li><strong>Privacy-First Design:</strong> AI processing with strict data protection and user control</li>
            </ul>
          </div>

          <div style={alertStyle}>
            <h4 style={{ margin: '0 0 8px 0' }}>
              Leveling the Playing Field
            </h4>
            <p style={{ margin: 0, fontSize: '14px' }}>
              Large corporations spend millions on AI consultants, custom development, and enterprise software. 
              Revfer gives small businesses access to the same caliber of AI technology at a fraction of the cost, 
              with no technical expertise required.
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'values',
      title: 'Our Values & Principles',
      icon: '💎',
      color: '#388e3c',
      content: (
        <div>
          <p style={{ marginBottom: '16px' }}>
            Our values guide every decision we make and every feature we build. They reflect our commitment to creating a platform that truly serves our community.
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', marginBottom: '24px' }}>
            <div style={{ ...cardStyle, backgroundColor: '#0f172a', color: 'white' }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                <span style={{ fontSize: '24px', marginRight: '12px' }}>🤖</span>
                <h4 style={{ margin: 0 }}>AI Democratization</h4>
              </div>
              <p style={{ margin: 0, fontSize: '14px' }}>
                We believe every business, regardless of size, deserves access to enterprise-level AI. 
                Technology should empower everyone, not just tech giants.
              </p>
            </div>

            <div style={{ ...cardStyle, backgroundColor: '#e8f5e8', color: '#388e3c' }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                <span style={{ fontSize: '24px', marginRight: '12px' }}>🔒</span>
                <h4 style={{ margin: 0 }}>Trust & Authenticity</h4>
              </div>
              <p style={{ margin: 0, fontSize: '14px' }}>
                Every feature we build prioritizes authentic experiences over engagement metrics. 
                Real reviews from real people create real value.
              </p>
            </div>

            <div style={{ ...cardStyle, backgroundColor: '#e3f2fd', color: '#1976d2' }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                <span style={{ fontSize: '24px', marginRight: '12px' }}>🌍</span>
                <h4 style={{ margin: 0 }}>Small Business First</h4>
              </div>
              <p style={{ margin: 0, fontSize: '14px' }}>
                Our platform exists to empower small and local businesses with the same tools 
                that large corporations use to dominate markets.
              </p>
            </div>

            <div style={{ ...cardStyle, backgroundColor: '#f3e5f5', color: '#7b1fa2' }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                <span style={{ fontSize: '24px', marginRight: '12px' }}>🔍</span>
                <h4 style={{ margin: 0 }}>Radical Transparency</h4>
              </div>
              <p style={{ margin: 0, fontSize: '14px' }}>
                We believe in complete transparency about our algorithms, data practices, 
                and business relationships. No hidden agendas or black boxes.
              </p>
            </div>

            <div style={{ ...cardStyle, backgroundColor: '#fff3e0', color: '#f57c00' }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                <span style={{ fontSize: '24px', marginRight: '12px' }}>⚡</span>
                <h4 style={{ margin: 0 }}>Innovation with Purpose</h4>
              </div>
              <p style={{ margin: 0, fontSize: '14px' }}>
                We embrace cutting-edge technology not for its own sake, but to solve 
                real problems and create meaningful value for small businesses.
              </p>
            </div>

            <div style={{ ...cardStyle, backgroundColor: '#fce7f3', color: '#ec4899' }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
                <span style={{ fontSize: '24px', marginRight: '12px' }}>🛡️</span>
                <h4 style={{ margin: 0 }}>Privacy by Design</h4>
              </div>
              <p style={{ margin: 0, fontSize: '14px' }}>
                Privacy isn't an afterthought—it's built into every AI feature from the ground up. 
                You control your data and how AI uses it.
              </p>
            </div>
          </div>

          <div style={successAlertStyle}>
            <h4 style={{ margin: '0 0 8px 0' }}>
              Values in Action
            </h4>
            <p style={{ margin: 0, fontSize: '14px' }}>
              These aren't just words on a page. Every product decision, policy change, and feature update 
              is evaluated against these principles to ensure we stay true to our mission.
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'impact',
      title: 'Community Impact & Success Stories',
      icon: '🌟',
      color: '#f57c00',
      content: (
        <div>
          <p style={{ marginBottom: '16px' }}>
            Revfer's impact extends beyond individual reviews to strengthen entire local business ecosystems and community connections.
          </p>

          <div style={cardStyle}>
            <h4 style={{ marginBottom: '12px', color: '#1976d2' }}>Supporting Local Businesses</h4>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li><strong>Authentic Discovery:</strong> Helping customers find businesses through trusted recommendations</li>
              <li><strong>Fair Representation:</strong> Reducing the impact of fake reviews on business reputation</li>
              <li><strong>Community Verification:</strong> Crowdsourced validation of business information and quality</li>
              <li><strong>Direct Engagement:</strong> Facilitating meaningful conversations between businesses and customers</li>
              <li><strong>Growth Insights:</strong> Providing businesses with authentic feedback for improvement</li>
            </ul>
          </div>

          <div style={cardStyle}>
            <h4 style={{ marginBottom: '12px', color: '#7b1fa2' }}>Empowering Consumers</h4>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li><strong>Trusted Recommendations:</strong> Reviews from people in your network and community</li>
              <li><strong>Informed Decisions:</strong> Rich context about reviewer relationships and credibility</li>
              <li><strong>AI-Assisted Discovery:</strong> Intelligent matching with businesses that fit your specific needs</li>
              <li><strong>Community Building:</strong> Connecting with others who share similar business experiences</li>
              <li><strong>Voice Amplification:</strong> AI assistance to help articulate detailed, helpful reviews</li>
            </ul>
          </div>

          <div style={{ ...cardStyle, backgroundColor: '#e8f5e8', color: '#388e3c' }}>
            <h4 style={{ marginBottom: '12px' }}>Platform Readiness</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '16px' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '4px' }}>1M+</div>
                <div style={{ fontSize: '14px' }}>Seeded Businesses</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '4px' }}>50+</div>
                <div style={{ fontSize: '14px' }}>AI Models Integrated</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '4px' }}>24/7</div>
                <div style={{ fontSize: '14px' }}>AI Processing</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '4px' }}>100%</div>
                <div style={{ fontSize: '14px' }}>Privacy Focused</div>
              </div>
            </div>
          </div>

          <div style={cardStyle}>
            <h4 style={{ marginBottom: '12px', color: '#f57c00' }}>Early Access Feedback</h4>
            <div style={{ marginBottom: '16px' }}>
              <p style={{ margin: '0 0 8px 0', fontSize: '14px', fontStyle: 'italic' }}>
                "Finally, an AI platform built for businesses like mine. The document analysis alone 
                saves me hours every week, and the customer insights are incredibly valuable."
              </p>
              <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>
                — Beta Business Owner, Local Service Provider
              </p>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <p style={{ margin: '0 0 8px 0', fontSize: '14px', fontStyle: 'italic' }}>
                "The AI content generation is like having a marketing team I could never afford. 
                It understands my business and helps me communicate with customers professionally."
              </p>
              <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>
                — Early Access User, Local Restaurant Owner
              </p>
            </div>
          </div>

          <div style={alertStyle}>
            <h4 style={{ margin: '0 0 8px 0' }}>
              Ready for Launch
            </h4>
            <p style={{ margin: 0, fontSize: '14px' }}>
              Our platform is fully built and ready to empower small businesses with enterprise-level AI. 
              We've pre-seeded 1M+ businesses and integrated cutting-edge AI models to provide immediate value from day one.
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'supporters-partners',
      title: 'Supporters & Partners',
      icon: '🤝',
      color: '#2563eb',
      content: (
        <div>
          <p style={{ marginBottom: '16px' }}>
            Revfer is proud to work with amazing partners and supporters who share our mission of democratizing AI for small businesses and building authentic community connections.
          </p>

          <div style={cardStyle}>
            <h4 style={{ marginBottom: '12px', color: '#2563eb' }}>Technology Partners</h4>
            <p style={{ marginBottom: '16px', fontSize: '14px' }}>
              We partner with leading technology companies to provide enterprise-level tools and infrastructure for small businesses.
            </p>
            
            {/* LiveAgent Partner Section */}
            {/* LiveAgent Partner Section - IMPROVED */}
            <div style={{ 
  backgroundColor: '#fff', 
  padding: '20px', 
  borderRadius: '8px', 
  border: '1px solid #e0e0e0',
  marginBottom: '16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: '16px'
}}>
  <div style={{ flex: 1, minWidth: '200px' }}>
    <h5 style={{ margin: '0 0 8px 0', color: '#1976d2', fontSize: '16px' }}>Customer Support Excellence</h5>
    <div className="liveagent-startups" style={{ fontSize: '14px', color: '#666' }}>
      <p style={{ margin: '0 0 12px 0' }}>
        <a 
          href="https://www.liveagent.com/help-desk-software/?utm_campaign=liveagent-startups" 
          title="LiveAgent" 
          target="_blank" 
          style={{ textDecoration: 'none', color: '#1976d2', fontWeight: '500' }}
        >
          Help desk software
        </a> provided by
      </p>
      <a 
        href="https://www.liveagent.com/?utm_campaign=liveagent-startups" 
        title="LiveAgent" 
        target="_blank"
        style={{ 
          display: 'inline-block', 
          padding: '12px 16px',
          backgroundColor: '#ffffff',
          borderRadius: '6px',
          border: '1px solid #e0e0e0',
          textDecoration: 'none',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}
      >
        <img 
          src="https://www.liveagent.com/app/themes/liveagent/assets/images/logo_liveagent.svg" 
          alt="LiveAgent" 
          style={{ 
            width: '120px',           // Fixed width instead of max-width
            height: '40px',           // Fixed height to ensure visibility
            display: 'block',
            objectFit: 'contain',     // Maintain aspect ratio
            objectPosition: 'left center'  // Align to left
          }}
          onError={(e) => {
            // Better fallback with proper styling
            const fallback = document.createElement('div');
            fallback.innerHTML = 'LiveAgent';
            fallback.style.cssText = `
              width: 120px;
              height: 40px;
              display: flex;
              align-items: center;
              justify-content: center;
              background-color: #ff6b00;
              color: white;
              font-weight: bold;
              font-size: 16px;
              border-radius: 4px;
            `;
            e.target.parentNode.replaceChild(fallback, e.target);
          }}
        />
      </a>
    </div>
  </div>
  <div style={{ minWidth: '200px', textAlign: 'right' }}>
    <p style={{ margin: 0, fontSize: '14px', color: '#666', fontStyle: 'italic' }}>
      "Empowering our customer support with professional help desk solutions"
    </p>
  </div>
</div>





            <div style={{ 
              backgroundColor: '#f8f9fa', 
              padding: '16px', 
              borderRadius: '8px',
              border: '1px solid #e9ecef'
            }}>
              <h5 style={{ margin: '0 0 8px 0', color: '#6c757d', fontSize: '14px' }}>Additional Technology Partners</h5>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px', fontSize: '14px', color: '#6c757d' }}>
                <div>• AWS (Cloud Infrastructure)</div>
                <div>• OpenAI (AI Models)</div>
                <div>• MongoDB (Database)</div>
                <div>• Stripe (Payments)</div>
              </div>
            </div>
          </div>

          <div style={cardStyle}>
            <h4 style={{ marginBottom: '12px', color: '#7b1fa2' }}>Community Supporters</h4>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li><strong>Local Business Associations:</strong> Organizations supporting small business innovation and AI adoption</li>
              <li><strong>Startup Accelerators:</strong> Programs focused on AI democratization and community technology</li>
              <li><strong>Open Source Contributors:</strong> Developers and researchers contributing to our AI tools and platforms</li>
              <li><strong>Beta Business Community:</strong> Early adopters helping shape our AI features and user experience</li>
            </ul>
          </div>

          <div style={cardStyle}>
            <h4 style={{ marginBottom: '12px', color: '#388e3c' }}>Academic & Research Partners</h4>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li><strong>AI Research Institutions:</strong> Collaborating on ethical AI development and small business applications</li>
              <li><strong>Business Schools:</strong> Partnering on case studies and research about AI adoption in local businesses</li>
              <li><strong>Community Development Programs:</strong> Working with organizations focused on local economic growth</li>
              <li><strong>Ethics Advisory Board:</strong> Ensuring responsible AI development and community trust</li>
            </ul>
          </div>

          <div style={cardStyle}>
            <h4 style={{ marginBottom: '12px', color: '#f57c00' }}>Startup Ecosystem</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minWidth(200px, 1fr))', gap: '16px' }}>
              <div>
                <h5 style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#f57c00' }}>Incubators & Accelerators</h5>
                <p style={{ margin: 0, fontSize: '13px', color: '#666' }}>
                  Programs supporting AI innovation for small business solutions
                </p>
              </div>
              <div>
                <h5 style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#f57c00' }}>Mentorship Network</h5>
                <p style={{ margin: 0, fontSize: '13px', color: '#666' }}>
                  Industry experts guiding our mission and product development
                </p>
              </div>
              <div>
                <h5 style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#f57c00' }}>Investment Partners</h5>
                <p style={{ margin: 0, fontSize: '13px', color: '#666' }}>
                  Mission-aligned investors supporting AI democratization
                </p>
              </div>
            </div>
          </div>

          <div style={successAlertStyle}>
            <h4 style={{ margin: '0 0 8px 0' }}>
              Become a Partner
            </h4>
            <p style={{ margin: 0, fontSize: '14px' }}>
              Interested in partnering with Revfer to democratize AI for small businesses? 
              We're always looking for organizations that share our mission. 
              Contact us at <strong>partnerships@revfer.com</strong> to explore collaboration opportunities.
            </p>
          </div>
        </div>
      )
    },
    ...(showSection ? [{
      id: 'team-leadership',
      title: 'Team & Leadership',
      icon: '👥',
      color: '#e65100',
      content: (
        <div>
          <p style={{ marginBottom: '16px' }}>
            Revfer is built by a diverse team of technologists, community builders, and business experts who share a passion for authentic connections and local commerce.
          </p>

          <div style={cardStyle}>
            <h4 style={{ marginBottom: '12px', color: '#1976d2' }}>Leadership Team</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
              <div style={{ backgroundColor: '#fff', padding: '16px', borderRadius: '8px', border: '1px solid #e0e0e0' }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                  <div style={{ 
                    width: '48px', 
                    height: '48px', 
                    borderRadius: '50%', 
                    backgroundColor: '#6366f1', 
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: '12px',
                    fontSize: '20px',
                    fontWeight: 'bold'
                  }}>
                    AJ
                  </div>
                  <div>
                    <h5 style={{ margin: 0, fontSize: '16px' }}>Pankaj Aggarwal</h5>
                    <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>Founder</p>
                  </div>
                </div>
                <p style={{ margin: 0, fontSize: '14px' }}>
                  Former product leader at local discovery platforms. Passionate about authentic community connections.
                </p>
              </div>

              <div style={{ backgroundColor: '#fff', padding: '16px', borderRadius: '8px', border: '1px solid #e0e0e0' }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                  <div style={{ 
                    width: '48px', 
                    height: '48px', 
                    borderRadius: '50%', 
                    backgroundColor: '#8b5cf6', 
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: '12px',
                    fontSize: '20px',
                    fontWeight: 'bold'
                  }}>
                    SC
                  </div>
                  <div>
                    <h5 style={{ margin: 0, fontSize: '16px' }}></h5>
                    <p style={{ margin: 0, fontSize: '14px', color: '#666' }}></p>
                  </div>
                </div>
                <p style={{ margin: 0, fontSize: '14px' }}>
                  AI researcher and engineer.
                </p>
              </div>

              <div style={{ backgroundColor: '#fff', padding: '16px', borderRadius: '8px', border: '1px solid #e0e0e0' }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                  <div style={{ 
                    width: '48px', 
                    height: '48px', 
                    borderRadius: '50%', 
                    backgroundColor: '#ec4899', 
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: '12px',
                    fontSize: '20px',
                    fontWeight: 'bold'
                  }}>
                    MR
                  </div>
                  <div>
                    <h5 style={{ margin: 0, fontSize: '16px' }}>Marcus Rodriguez</h5>
                    <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>VP of Community</p>
                  </div>
                </div>
                <p style={{ margin: 0, fontSize: '14px' }}>
                  Community building expert focused on creating inclusive, trustworthy online environments.
                </p>
              </div>
            </div>
          </div>

          <div style={cardStyle}>
            <h4 style={{ marginBottom: '12px', color: '#7b1fa2' }}>Our Team Culture</h4>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li><strong>Remote-First:</strong> Distributed team with members across different time zones and communities</li>
              <li><strong>Community-Connected:</strong> Team members actively participate in local business ecosystems</li>
              <li><strong>Diverse Perspectives:</strong> Bringing together experiences from different industries and backgrounds</li>
              <li><strong>Continuous Learning:</strong> Regular sharing of insights about trust, AI, and community building</li>
              <li><strong>User-Focused:</strong> Regular engagement with our community to understand real needs</li>
            </ul>
          </div>

          <div style={cardStyle}>
            <h4 style={{ marginBottom: '12px', color: '#388e3c' }}>Advisory & Investors</h4>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li><strong>Industry Experts:</strong> Advisors from local commerce, AI research, and community platforms</li>
              <li><strong>Community Leaders:</strong> Business owners and community organizers who guide our mission</li>
              <li><strong>Technical Advisors:</strong> AI researchers and privacy experts ensuring responsible innovation</li>
              <li><strong>Mission-Aligned Investors:</strong> Funding partners who share our vision for authentic business discovery</li>
            </ul>
          </div>

          <div style={successAlertStyle}>
            <h4 style={{ margin: '0 0 8px 0' }}>
              Join Our Team
            </h4>
            <p style={{ margin: 0, fontSize: '14px' }}>
              We're always looking for passionate individuals who want to help build the future of trusted business discovery. 
              Check our careers page for current opportunities.
            </p>
          </div>
        </div>
      )
    }] : []),
    {
      id: 'future-vision',
      title: 'Future Vision & Roadmap',
      icon: '🔮',
      color: '#795548',
      content: (
        <div>
          <p style={{ marginBottom: '16px' }}>
            Our vision extends beyond reviews to create a comprehensive ecosystem where trust, technology, and community converge to transform how people discover and connect with businesses.
          </p>

          <div style={cardStyle}>
            <h4 style={{ marginBottom: '12px', color: '#1976d2' }}>Near-Term Innovations (6-12 months)</h4>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li><strong>Advanced AI Conversations:</strong> Natural language queries about businesses with context-aware responses</li>
              <li><strong>Video Reviews:</strong> Rich multimedia reviews with AI-powered highlights and summaries</li>
              <li><strong>Business Matching:</strong> Intelligent pairing of customers with businesses based on specific needs</li>
              <li><strong>Mobile App Launch:</strong> Native mobile experience with location-based features</li>
              <li><strong>Integration APIs:</strong> Allow businesses to integrate Revfer insights into their operations</li>
            </ul>
          </div>

          <div style={cardStyle}>
            <h4 style={{ marginBottom: '12px', color: '#7b1fa2' }}>Medium-Term Goals (1-2 years)</h4>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li><strong>Global Expansion:</strong> Localized versions for international markets with cultural adaptation</li>
              <li><strong>Industry Specialization:</strong> Tailored experiences for specific sectors (healthcare, hospitality, professional services)</li>
              <li><strong>Predictive Analytics:</strong> Help businesses anticipate trends and optimize their services</li>
              <li><strong>Blockchain Verification:</strong> Immutable trust records and verified reviewer credentials</li>
              <li><strong>Community Governance:</strong> User-driven platform policies and community moderation</li>
            </ul>
          </div>

          <div style={cardStyle}>
            <h4 style={{ marginBottom: '12px', color: '#388e3c' }}>Long-Term Vision (3-5 years)</h4>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li><strong>Local Economy Platform:</strong> Complete ecosystem supporting local business growth and community development</li>
              <li><strong>AI Business Advisors:</strong> Intelligent assistants helping businesses improve based on community feedback</li>
              <li><strong>Trust-Based Commerce:</strong> Direct integration with e-commerce and service booking platforms</li>
              <li><strong>Community Development Tools:</strong> Resources for local governments and organizations to strengthen business districts</li>
              <li><strong>Educational Initiatives:</strong> Programs teaching digital literacy and authentic online engagement</li>
            </ul>
          </div>

          <div style={{ ...cardStyle, backgroundColor: '#ede9fe', color: '#6366f1' }}>
            <h4 style={{ marginBottom: '12px' }}>Emerging Technology Integration</h4>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li><strong>Augmented Reality:</strong> AR overlays showing trust-based business information in real-world contexts</li>
              <li><strong>Voice Interfaces:</strong> Natural language interaction for accessibility and convenience</li>
              <li><strong>IoT Integration:</strong> Smart city infrastructure providing real-time business availability and service quality</li>
              <li><strong>Advanced AI Models:</strong> Continuously evolving recommendation and trust algorithms</li>
            </ul>
          </div>

          <div style={alertStyle}>
            <h4 style={{ margin: '0 0 8px 0' }}>
              Community-Driven Development
            </h4>
            <p style={{ margin: 0, fontSize: '14px' }}>
              Our roadmap is shaped by community feedback, user research, and emerging needs in local business ecosystems. 
              We regularly survey our users and adapt our priorities based on real-world impact potential.
            </p>
          </div>
        </div>
      )
    },
    {
      id: 'contact-join',
      title: 'Get Involved & Contact Us',
      icon: '📞',
      color: '#607d8b',
      content: (
        <div>
          <p style={{ marginBottom: '16px' }}>
            Ready to experience the power of AI for your business? Here are the ways to get started with Revfer.
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', marginBottom: '24px' }}>
            <div style={{ ...cardStyle, backgroundColor: '#e3f2fd', color: '#1976d2' }}>
              <h4 style={{ marginBottom: '8px' }}>Join Our AI Platform</h4>
              <p style={{ margin: '0 0 12px 0', fontSize: '14px' }}>
                Start using enterprise-level AI tools to grow your business and connect with customers.
              </p>
              <button 
                style={{ ...primaryButtonStyle, backgroundColor: '#1976d2' }}
                onClick={() => {
                  // Check if user is authenticated (this would need to be implemented based on  auth system)
                  const isAuthenticated = authStatus === 'authenticated'; 
                  if (isAuthenticated) {
                    window.location.href = '/home';
                  } else {
                    window.location.href = '/auth/signin';
                  }
                }}
              >
                Get Started Now
              </button>
            </div>

            <div style={{ ...cardStyle, backgroundColor: '#f3e5f5', color: '#7b1fa2' }}>
              <h4 style={{ marginBottom: '8px' }}>Business Owners</h4>
              <p style={{ margin: '0 0 12px 0', fontSize: '14px' }}>
                Add your business and start leveraging AI for customer insights and growth.
              </p>
              <button 
                style={{ ...primaryButtonStyle, backgroundColor: '#7b1fa2' }}
                onClick={() => window.location.href = '/company/new'}
              >
                Add Your Business
              </button>
            </div>

            <div style={{ ...cardStyle, backgroundColor: '#fff3e0', color: '#f57c00' }}>
              <h4 style={{ marginBottom: '8px' }}>Career Opportunities</h4>
              <p style={{ margin: '0 0 12px 0', fontSize: '14px' }}>
                Join our mission to democratize AI for small businesses worldwide.
              </p>
              <button 
                style={{ ...primaryButtonStyle, backgroundColor: '#f57c00' }}
                onClick={() => window.location.href = 'mailto:careers@revfer.com?subject=Career Opportunity Inquiry'}
              >
                Email Us: careers@revfer.com
              </button>
            </div>
          </div>

          <div style={cardStyle}>
            <h4 style={{ marginBottom: '12px', color: '#6366f1' }}>Contact Information</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
              <div>
                <h5 style={{ margin: '0 0 8px 0', color: '#1976d2' }}>General Inquiries</h5>
                <p style={{ margin: 0, fontSize: '14px' }}>
                  Email: hello@revfer.com<br />
                  Response: Within 24 hours
                </p>
              </div>
              <div>
                <h5 style={{ margin: '0 0 8px 0', color: '#7b1fa2' }}>Business Partnerships</h5>
                <p style={{ margin: 0, fontSize: '14px' }}>
                  Email: partnerships@revfer.com<br />
                  Response: Within 48 hours
                </p>
              </div>
              <div>
                <h5 style={{ margin: '0 0 8px 0', color: '#388e3c' }}>Media & Press</h5>
                <p style={{ margin: 0, fontSize: '14px' }}>
                  Email: press@revfer.com<br />
                  Response: Within 24 hours
                </p>
              </div>
              <div>
                <h5 style={{ margin: '0 0 8px 0', color: '#f57c00' }}>AI & Technical</h5>
                <p style={{ margin: 0, fontSize: '14px' }}>
                  Email: ai@revfer.com<br />
                  Response: Within 48 hours
                </p>
              </div>
            </div>
          </div>

          <div style={cardStyle}>
            <h4 style={{ marginBottom: '12px', color: '#795548' }}>Office & Headquarters</h4>
            <p style={{ margin: 0, fontSize: '14px' }}>
              <strong>Revfer Inc.</strong><br />
              59 Grover Rd<br />
              Ashland, MA 01721<br />
              United States<br /><br />
             
            </p>
          </div>

          <div style={successAlertStyle}>
            <h4 style={{ margin: '0 0 8px 0' }}>
              Stay Connected
            </h4>
            <p style={{ margin: 0, fontSize: '14px' }}>
              Follow us on social media for product updates, community highlights, and insights about the future of business discovery. 
              Join our newsletter for monthly updates on platform developments and success stories.
            </p>
          </div>
        </div>
      )
    }
  ];

  return (
    <>
      {/* 🚀 COMPREHENSIVE SEO OPTIMIZATION WITH STRUCTURED DATA */}
      <EnhancedSEO 
        title="About Revfer - AI Platform Democratizing Enterprise Technology for Small Businesses"
        description="Learn how Revfer democratizes enterprise-level AI and machine learning for small businesses. From intelligent customer insights to automated content generation, we make advanced technology accessible and affordable for local businesses that couldn't access it before."
        keywords="AI for small business, artificial intelligence platform, machine learning for local businesses, enterprise AI tools, small business automation, customer insights AI, content generation AI, business intelligence platform, professional networking AI, local business technology, democratizing AI, LLM for business, predictive analytics small business, AI-powered reviews, automated business insights, small business AI software, enterprise technology affordable, AI business platform, machine learning tools, intelligent business solutions"
        type="organization"
        image="/images/revfer-ai-platform-og.jpg"
        author="Revfer Team"
        dateModified={new Date().toISOString()}
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'About Us', url: '/legal/about' }
        ]}
        organizationData={{
          "@context": "https://schema.org",
          "@type": ["TechnologyCompany", "SoftwareApplication"],
          "name": "Revfer",
          "alternateName": "Revfer Inc",
          "description": "AI platform democratizing enterprise-level technology for small and local businesses through intelligent automation, customer insights, and professional networking",
          "url": "https://www.revfer.com",
          "logo": {
            "@type": "ImageObject",
            "url": "https://www.revfer.com/logo.png",
            "caption": "Revfer AI Platform Logo"
          },
          "foundingDate": "2022",
          "foundingLocation": {
            "@type": "Place",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Boston",
              "addressRegion": "MA",
              "addressCountry": "US"
            }
          },
          "founders": [
            {
              "@type": "Person",
              "name": "Pankaj Aggarwal",
              "jobTitle": "Founder",
              "description": "Former Engineering leader at companies like Bose, Motorola and Thermo Fisher "
            },
            
          ],
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "59 Grover Rd",
            "addressLocality": "Ashland",
            "addressRegion": "MA",
            "postalCode": "01721",
            "addressCountry": "US"
          },
          "contactPoint": [
            {
              "@type": "ContactPoint",
              "contactType": "customer service",
              "email": "hello@revfer.com",
              "availableLanguage": "English"
            },
            {
              "@type": "ContactPoint",
              "contactType": "sales",
              "email": "partnerships@revfer.com"
            },
            {
              "@type": "ContactPoint",
              "contactType": "technical support",
              "email": "ai@revfer.com"
            }
          ],
          "sameAs": [
            "https://twitter.com/revfer",
            "https://linkedin.com/company/revfer"
          ],
          "industry": ["Artificial Intelligence", "Business Intelligence", "Professional Networking", "SaaS"],
          "numberOfEmployees": "11-50",
          "keywords": ["AI for small business", "machine learning", "business intelligence", "professional networking", "enterprise AI", "local business technology", "automated insights", "customer analytics"],
          "applicationCategory": "BusinessApplication",
          "operatingSystem": "Web Browser, iOS, Android",
          "applicationSubCategory": "Business Intelligence, CRM, Professional Networking",
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "4.8",
            "ratingCount": "150",
            "bestRating": "5"
          },
          "offers": [
            {
              "@type": "Offer",
              "name": "AI Business Intelligence Platform",
              "description": "Enterprise-level AI tools for small businesses including customer insights, content generation, and predictive analytics",
              "category": "SaaS",
              "businessFunction": "Business Intelligence, Customer Analytics, Content Creation",
              "availability": "InStock"
            }
          ],
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "AI Tools for Small Business",
            "itemListElement": [
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "SoftwareApplication",
                  "name": "Customer Intelligence AI",
                  "description": "AI-powered insights into customer behavior and preferences"
                }
              },
              {
                "@type": "Offer", 
                "itemOffered": {
                  "@type": "SoftwareApplication",
                  "name": "Content Generation AI",
                  "description": "LLM-powered tools for creating marketing content and communications"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "SoftwareApplication", 
                  "name": "Predictive Analytics",
                  "description": "Machine learning insights for business trends and opportunities"
                }
              }
            ]
          },
          "knowsAbout": [
            "Artificial Intelligence",
            "Machine Learning", 
            "Natural Language Processing",
            "Business Intelligence",
            "Customer Analytics",
            "Professional Networking",
            "Small Business Technology",
            "Enterprise AI",
            "Automated Insights",
            "Trust Networks"
          ],
          "seeks": "Small businesses, local businesses, professional service providers seeking AI-powered business intelligence and networking solutions"
        }}
      />

      <div style={containerStyle}>
        {/* AI Platform Banner */}
        <div style={{ 
          backgroundColor: '#0f172a', 
          color: 'white', 
          padding: '24px', 
          borderRadius: '8px', 
          marginBottom: '16px',
          textAlign: 'center',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '12px' }}>
            <span style={{ fontSize: '32px', marginRight: '12px' }}>🤖</span>
            <h2 style={{ margin: 0, fontSize: '28px', fontWeight: '600' }}>
              Democratizing AI for Small & Local Businesses
            </h2>
          </div>
          <p style={{ margin: 0, fontSize: '18px', opacity: 0.9, maxWidth: '900px', margin: '0 auto' }}>
            Revfer brings enterprise-level AI and LLM capabilities to small and local businesses. 
            From intelligent customer insights to AI-powered content generation, we're making advanced technology 
            accessible to businesses that couldn't afford it before.
          </p>
        </div>

        {/* Header */}
        <div style={headerStyle}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
            <span style={{ fontSize: '40px', marginRight: '16px' }}>🚀</span>
            <h1 style={{ margin: 0, fontSize: '48px', fontWeight: '300' }}>
              About Revfer
            </h1>
          </div>
          <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '400', opacity: 0.9 }}>
            The AI platform empowering small businesses with enterprise-level intelligence, 
            authentic community connections, and trusted business discovery.
          </h2>
          <div style={{ 
            display: 'inline-block', 
            marginTop: '16px',
            padding: '8px 16px',
            backgroundColor: 'rgba(255,255,255,0.2)',
            borderRadius: '16px',
            fontSize: '14px'
          }}>
            Founded in 2022 • Boston, MA
          </div>
        </div>

        {/* Mission Statement Highlight */}
        <div style={{ ...paperStyle, backgroundColor: '#ede9fe', border: '2px solid #6366f1' }}>
          <div style={{ textAlign: 'center', padding: '16px' }}>
            <h3 style={{ marginBottom: '16px', color: '#6366f1', fontSize: '24px' }}>
              Our Mission
            </h3>
            <p style={{ margin: 0, fontSize: '18px', color: '#6366f1', fontWeight: '500', maxWidth: '800px', margin: '0 auto' }}>
              To democratize artificial intelligence and advanced technology for small and local businesses, 
              while building the world's most trusted business discovery platform through authentic community connections.
            </p>
          </div>
        </div>

        {/* Quick Navigation */}
        <div style={paperStyle}>
          <h3 style={{ marginBottom: '16px', color: '#6366f1' }}>
            Explore Our Story
          </h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {sections.map((section) => (
              <span
                key={section.id}
                style={{
                  ...chipStyle,
                  backgroundColor: expandedSection === section.id ? section.color : '#f5f5f5',
                  color: expandedSection === section.id ? 'white' : '#666',
                  border: expandedSection === section.id ? 'none' : '1px solid #e0e0e0'
                }}
                onClick={() => setExpandedSection(section.id)}
              >
                {section.icon} {section.title}
              </span>
            ))}
          </div>
        </div>

        {/* Main Content */}
        <div>
          {sections.map((section) => (
            <div key={section.id} style={accordionStyle}>
              <div
                style={{
                  ...accordionHeaderStyle,
                  backgroundColor: expandedSection === section.id ? '#f5f5f5' : '#fafafa'
                }}
                onClick={() => handleAccordionToggle(section.id)}
              >
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span style={{ fontSize: '24px', marginRight: '12px', color: section.color }}>
                    {section.icon}
                  </span>
                  <h3 style={{ margin: 0, fontSize: '20px' }}>
                    {section.title}
                  </h3>
                </div>
                <span style={{ fontSize: '20px', color: '#666' }}>
                  {expandedSection === section.id ? '−' : '+'}
                </span>
              </div>
              {expandedSection === section.id && (
                <div style={accordionContentStyle}>
                  {section.content}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Footer CTA */}
        {showSection && 
        <div style={{ ...paperStyle, backgroundColor: '#f5f5f5', marginTop: '32px', textAlign: 'center' }}>
        <h3 style={{ marginBottom: '16px', color: '#6366f1' }}>
          Ready to Unlock AI for Your Business?
        </h3>
        <p style={{ marginBottom: '24px', fontSize: '16px' }}>
          Join thousands of small businesses already using enterprise-level AI tools to grow their operations, 
          understand their customers better, and compete with larger companies.
        </p>
        <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '12px' }}>
          <button style={{ ...primaryButtonStyle, fontSize: '16px', padding: '16px 32px' }}>
            Start Using AI Today
          </button>
          <button style={{ ...outlinedButtonStyle, fontSize: '16px', padding: '16px 32px' }}>
            Explore AI Features
          </button>
          <button style={{ ...outlinedButtonStyle, fontSize: '16px', padding: '16px 32px' }}>
            Contact Our AI Team
          </button>
        </div>
      </div>
        }
        
      </div>
    </>
  );
};

export default AboutPage;